import React from 'react'
import {defineMessages, useIntl} from 'react-intl'
import styled from '@emotion/styled'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {PageWidthContainer} from '@vio/atlas/helpers/PageWidthContainer'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {useGetBusinessUnitQuery} from '@vio/core/src/trustpilotWidget/services/trustpilotApi'
import {TrustpilotWidget} from '@vio/shared/src/search/TrustpilotWidget'

import {HomePageSectionTitle} from '../commonStyles'
import {HighlightedTitle} from '../HighlightedTitle'

const LayoutWrapper = styled.div`
  padding: ${cssTheme.layout.spacing.s950} ${cssTheme.layout.spacing.s600};
  overflow: hidden;

  ${linariaMq.desktopXs} {
    padding-top: calc(${cssTheme.layout.spacing.s950} * 2);
    padding-bottom: calc(${cssTheme.layout.spacing.s950} * 2);
  }
`

const LeftContentWrapper = styled.div`
  ${linariaMq.desktopXs} {
    min-width: 40%;
    padding-top: ${cssTheme.layout.spacing.s950};
  }
`

const supports = defineMessages({
  realTravelExpertsTitle: {
    id: 'homePage.supportRealTravelExpertsTitle',
    defaultMessage: 'Real travel experts'
  },
  realTravelExpertsSubtitle: {
    id: 'support.RealTravelExpertsSubtitle',
    defaultMessage: "We've been there, done it, got the T-shirt."
  },
  alwaysThereForYouTitle: {
    id: 'homePage.supportAlwaysThereForYouTitle',
    defaultMessage: 'Always there for you'
  },
  alwaysThereForYouSubtitle: {
    id: 'homePage.supportAlwaysThereForYouSubtitle',
    defaultMessage: 'Our response times are among the fastest in the industry.'
  },
  efficientAnEffectiveTitle: {
    id: 'homePage.supportEfficientAnEffectiveTitle',
    defaultMessage: 'Efficient and effective'
  },
  efficientAnEffectiveSubtitle: {
    id: 'homePage.supportEfficientSubtitle',
    defaultMessage: "We do the hard work, so you don't have to."
  }
})

const supportPerks = [
  {
    title: supports.realTravelExpertsTitle,
    subtitle: supports.realTravelExpertsSubtitle
  },
  {
    title: supports.alwaysThereForYouTitle,
    subtitle: supports.alwaysThereForYouSubtitle
  },
  {
    title: supports.efficientAnEffectiveTitle,
    subtitle: supports.efficientAnEffectiveSubtitle
  }
]

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} fill="none">
    <rect width={20} height={20} fill="#3E9C32" rx={10} />
    <path
      fill="#fff"
      fillRule="evenodd"
      d="M15.013 6.165c.242.226.25.6.017.834l-6.753 6.82a.617.617 0 0 1-.89-.014l-2.431-2.623A.578.578 0 0 1 5 10.348a.62.62 0 0 1 .858.044l1.994 2.152 6.3-6.363a.62.62 0 0 1 .86-.016Z"
      clipRule="evenodd"
    />
  </svg>
)

export const SupportSection = () => {
  const {shell} = useBrandCapabilities()
  const {data, isLoading} = useGetBusinessUnitQuery(undefined, {
    skip: !shell.hasTrustpilot
  })
  const {isMobile} = useDeviceLayout()
  const {formatMessage} = useIntl()

  return (
    <PageWidthContainer>
      <LayoutWrapper>
        <Box
          display="flex"
          flexDirection={isMobile ? 'column' : 'row'}
          gap="s700"
        >
          <LeftContentWrapper>
            <Box paddingBottom="s600">
              <TrustpilotWidget
                isLoading={isLoading}
                variant="badge"
                numberOfReviews={data?.numberOfReviews?.total}
                stars={data?.score?.stars}
                trustScore={data?.score?.trustScore}
              />
            </Box>
            <HomePageSectionTitle variant={isMobile ? 'titleXL' : 'title4XL'}>
              <HighlightedTitle source="ExceptionalCustomerSupport" />
            </HomePageSectionTitle>
            <Box paddingTop="s800" paddingBottom={isMobile ? 's000' : 's950'}>
              <Box display="flex" flexDirection="column" gap="s700">
                {supportPerks.map((perk, index) => (
                  <Box display="flex" key={index} gap="s500">
                    <Box paddingTop="s200">
                      <CheckIcon />
                    </Box>
                    <div>
                      <Text variant="titleL">{formatMessage(perk.title)}</Text>
                      <Text variant="bodyM">
                        {formatMessage(perk.subtitle)}
                      </Text>
                    </div>
                  </Box>
                ))}
              </Box>
            </Box>
          </LeftContentWrapper>

          <div>
            <Box display="flex" flexDirection="column" gap="s700">
              <Box display="flex" gap="s700">
                <img
                  src="/static/support-section/support-1.jpg"
                  width={160}
                  alt="Vio.com customer support"
                />
                <img
                  src="/static/support-section/support-2.jpg"
                  width={352}
                  alt="Vio.com customer support"
                />
              </Box>
              <Box display="flex" gap="s700">
                <img
                  src="/static/support-section/support-3.jpg"
                  width={352}
                  alt="Vio.com customer support"
                />
                <img
                  src="/static/support-section/support-4.jpg"
                  width={160}
                  alt="Vio.com customer support"
                />
              </Box>
            </Box>
          </div>
        </Box>
      </LayoutWrapper>
    </PageWidthContainer>
  )
}
